<template>
  <div class="event-layout">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="page-header">
        <div>
          <p class="breadcrumb">Events → {{ eventName }}</p>
          <h1 class="page-title">Orator Prep</h1>
          <p class="page-subtitle">
            Prepare live pronunciation notes without exposing student email addresses or review links.
          </p>
        </div>
        <button class="secondary-button" type="button" @click="loadEvent">
          Refresh
        </button>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>
      <div v-if="isPublished" class="notice warning">
        Orator notes are read-only after queue publish.
      </div>

      <section class="panel">
        <div class="prep-summary">
          <div>
            <h2 class="panel-title">Preparation List</h2>
            <p class="panel-copy">
              {{ prepRows.length }} rows {{ isPublished ? "published to the queue" : "in this event" }}.
            </p>
          </div>
          <span class="status-pill" :class="{ visible: isPublished, preview: !isPublished }">
            {{ isPublished ? "Published" : "Before publish" }}
          </span>
        </div>
        <div v-if="!isPublished && changedPrepRows.length" class="bulk-actions">
          <span class="bulk-copy">{{ changedPrepRows.length }} unsaved {{ changedPrepRows.length === 1 ? "prep edit" : "prep edits" }}</span>
          <button
            class="primary-button"
            type="button"
            :disabled="savingAllPrep"
            @click="saveChangedPrep"
          >
            {{ savingAllPrep ? "Saving..." : saveChangedPrepLabel }}
          </button>
        </div>

        <div v-if="loading" class="empty-panel">Loading Orator Prep...</div>
        <div v-else-if="!prepRows.length" class="empty-panel">
          No prep rows are available for this event yet.
        </div>
        <div v-else class="prep-list">
          <article v-for="row in prepRows" :key="row.rowKey" class="prep-row">
            <div class="prep-order">{{ row.queueOrder }}</div>

            <div class="prep-main">
              <div class="prep-heading">
                <div>
                  <h3 class="student-name">{{ row.type === "presenter_note" ? "Presenter note" : row.fullName }}</h3>
                  <p class="student-meta">{{ row.type === "presenter_note" ? row.text : row.award || "No award" }}</p>
                </div>
                <div class="status-stack">
                  <span v-if="hasPrepChanged(row)" class="status-pill unsaved">
                    Unsaved
                  </span>
                  <span v-if="row.type === 'presenter_note'" class="status-pill subtle">
                    Read only
                  </span>
                  <span v-if="row.type === 'student'" class="status-pill" :class="checkStatusClass(row.checkStatus)">
                    {{ checkStatusLabel(row.checkStatus) }}
                  </span>
                  <span v-if="row.type === 'student'" class="status-pill subtle">
                    {{ pronunciationStatusLabel(row.pronunciationStatus) }}
                  </span>
                </div>
              </div>

              <div v-if="row.type === 'presenter_note'" class="prep-note">
                {{ row.text }}
              </div>

              <div v-else class="prep-grid">
                <label>
                  <span>Live phonetics</span>
                  <input :value="row.activePhonetics || 'No phonetics yet'" type="text" class="field-input" disabled />
                </label>

                <label>
                  <span>Orator Notes</span>
                  <textarea
                    v-model="noteDrafts[row.participantId]"
                    class="field-input notes-input"
                    rows="2"
                    :disabled="isPublished || savingParticipantId === row.participantId"
                  ></textarea>
                </label>
              </div>

              <label v-if="row.type === 'student'" class="check-request-row">
                <input
                  v-model="checkDrafts[row.participantId]"
                  type="checkbox"
                  :disabled="isPublished || savingParticipantId === row.participantId"
                />
                <span>Ask at check-in</span>
              </label>

              <div v-if="row.type === 'student'" class="card-actions">
                <button
                  v-if="row.pronunciationStatus === 'ready'"
                  class="secondary-button"
                  type="button"
                  :disabled="playingParticipantId === row.participantId"
                  @click="playPronunciation(row)"
                >
                  {{ playingParticipantId === row.participantId ? "Playing..." : "Play Audio" }}
                </button>
                <button
                  class="secondary-button"
                  type="button"
                  :disabled="isPublished || !hasPrepChanged(row) || savingParticipantId === row.participantId || savingAllPrep"
                  @click="savePrep(row)"
                >
                  {{ savingParticipantId === row.participantId ? "Saving..." : "Save Student Prep" }}
                </button>
              </div>
            </div>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { fetchEvent, fetchPronunciationAudioUrl, updateParticipantRow } from "@/lib/api";
import EventSidebar from "./EventSidebar.vue";

export default {
  name: "OratorPrepView",
  components: {
    EventSidebar,
  },
  data() {
    return {
      snapshot: null,
      noteDrafts: {},
      checkDrafts: {},
      loading: true,
      savingParticipantId: "",
      savingAllPrep: false,
      playingParticipantId: "",
      error: "",
      successMessage: "",
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.snapshot?.event?.name || this.eventId;
    },
    isPublished() {
      return Boolean(this.snapshot?.participants?.locked || this.snapshot?.workflow?.queuePublished);
    },
    prepRows() {
      const rows = this.isPublished ? this.publishedStudentRows() : this.draftStudentRows();
      return rows.map((item, index) => {
        const pronunciation = item.pronunciation || {};
        const participantId = item.sourceParticipantId || item.id || "";
        return {
          rowKey: `${participantId}-${index}`,
          participantId,
          queueOrder: index + 1,
          type: item.type || "student",
          fullName: item.fullName || "",
          text: item.text || "",
          award: item.award || "",
          oratorNotes: item.oratorNotes || "",
          activePhonetics: this.activePhonetics(item),
          checkRequested: Boolean(pronunciation.checkRequested),
          checkStatus: pronunciation.checkStatus || "none",
          pronunciationStatus: pronunciation.status || "",
        };
      });
    },
    changedPrepRows() {
      return this.prepRows.filter((row) => this.hasPrepChanged(row));
    },
    saveChangedPrepLabel() {
      const count = this.changedPrepRows.length;
      return `Save ${count} Unsaved ${count === 1 ? "Prep Edit" : "Prep Edits"}`;
    },
  },
  async mounted() {
    await this.loadEvent();
  },
  methods: {
    async loadEvent() {
      this.loading = true;
      this.error = "";
      try {
        this.snapshot = await fetchEvent(this.eventId);
        this.noteDrafts = this.prepRows.reduce((drafts, row) => {
          drafts[row.participantId] = row.oratorNotes || "";
          return drafts;
        }, {});
        this.checkDrafts = this.prepRows.reduce((drafts, row) => {
          drafts[row.participantId] = row.checkRequested || row.checkStatus === "needs_check";
          return drafts;
        }, {});
      } catch (error) {
        this.error = "Unable to load Orator Prep.";
      } finally {
        this.loading = false;
      }
    },
    draftStudentRows() {
      return [...(this.snapshot?.participants?.items || [])]
        .filter((item) => item.type === "student" || item.type === "presenter_note")
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    publishedStudentRows() {
      return (this.snapshot?.queue?.items || []).filter(
        (item) => item.type === "student" || item.type === "presenter_note"
      );
    },
    activePhonetics(item) {
      const pronunciation = item.pronunciation || {};
      return (
        pronunciation.manualPhonetics ||
        pronunciation.activePhonetics ||
        item.presenterPhonetics ||
        pronunciation.phonetics ||
        pronunciation.generated?.phonetics ||
        ""
      );
    },
    checkStatusLabel(status) {
      return {
        none: "No check",
        needs_check: "Ask at check-in",
        checked_no_change: "Checked",
        changed_at_checkin: "Changed at check-in",
        unable_to_confirm: "Unable to confirm",
      }[status || "none"] || "No check";
    },
    checkStatusClass(status) {
      return {
        warning: status === "needs_check",
        visible: status === "changed_at_checkin",
        subtle: !status || status === "none",
      };
    },
    pronunciationStatusLabel(status) {
      return {
        queued: "Queued",
        processing: "Processing",
        ready: "Ready",
        failed: "Failed",
      }[status || ""] || "Missing";
    },
    hasPrepChanged(row) {
      if (row.type !== "student") {
        return false;
      }
      const noteChanged = (this.noteDrafts[row.participantId] || "") !== (row.oratorNotes || "");
      const checkChanged = Boolean(this.checkDrafts[row.participantId]) !== Boolean(row.checkRequested || row.checkStatus === "needs_check");
      return noteChanged || checkChanged;
    },
    applyParticipantsUpdate(participants) {
      if (!participants || !this.snapshot) {
        return;
      }
      this.snapshot = {
        ...this.snapshot,
        participants,
      };
      this.syncDraftsFromRows({ preserveExistingDrafts: true });
    },
    syncDraftsFromRows({ preserveExistingDrafts = false } = {}) {
      const nextNoteDrafts = {};
      const nextCheckDrafts = {};
      this.prepRows.forEach((row) => {
        nextNoteDrafts[row.participantId] =
          preserveExistingDrafts && this.noteDrafts[row.participantId] !== undefined
            ? this.noteDrafts[row.participantId]
            : row.oratorNotes || "";
        nextCheckDrafts[row.participantId] =
          preserveExistingDrafts && this.checkDrafts[row.participantId] !== undefined
            ? this.checkDrafts[row.participantId]
            : row.checkRequested || row.checkStatus === "needs_check";
      });
      this.noteDrafts = nextNoteDrafts;
      this.checkDrafts = nextCheckDrafts;
    },
    async savePrep(row) {
      this.savingParticipantId = row.participantId;
      this.error = "";
      this.successMessage = "";
      try {
        const result = await this.savePrepRow(row);
        this.successMessage = "Orator prep saved.";
        this.applyParticipantsUpdate(result.participants);
      } catch (error) {
        if (error?.response?.status === 400 && this.isPublished) {
          this.error = "Orator notes are read-only after queue publish.";
        } else {
          this.error = "Unable to save Orator notes.";
        }
      } finally {
        this.savingParticipantId = "";
      }
    },
    async savePrepRow(row) {
      const checkRequested = Boolean(this.checkDrafts[row.participantId]);
      return updateParticipantRow(this.eventId, row.participantId, {
        oratorNotes: this.noteDrafts[row.participantId] || "",
        pronunciation: {
          checkRequested,
          checkStatus: checkRequested ? "needs_check" : "none",
        },
      });
    },
    async saveChangedPrep() {
      if (!this.changedPrepRows.length) {
        return;
      }
      this.savingAllPrep = true;
      this.error = "";
      this.successMessage = "";
      try {
        const changedRows = [...this.changedPrepRows];
        for (const row of changedRows) {
          this.savingParticipantId = row.participantId;
          const result = await this.savePrepRow(row);
          this.applyParticipantsUpdate(result.participants);
        }
        this.successMessage = `${changedRows.length} Orator Prep changes saved.`;
      } catch (error) {
        if (error?.response?.status === 409) {
          this.error = "Someone else updated the roster. Refresh and try again.";
        } else {
          this.error = "Unable to save all Orator Prep changes.";
        }
      } finally {
        this.savingAllPrep = false;
        this.savingParticipantId = "";
      }
    },
    async playPronunciation(row) {
      if (!row?.participantId || row.pronunciationStatus !== "ready") {
        return;
      }
      this.playingParticipantId = row.participantId;
      this.error = "";
      try {
        const { url } = await fetchPronunciationAudioUrl(this.eventId, row.participantId);
        const audio = new Audio(url);
        await audio.play();
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to play pronunciation audio.";
      } finally {
        this.playingParticipantId = "";
      }
    },
  },
};
</script>

<style scoped>
.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 1rem 2rem;
}

.event-main {
  flex: 1;
  min-width: 0;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.breadcrumb,
.page-subtitle,
.panel-copy {
  color: var(--orator-graphite);
}

.page-title {
  margin: 0;
  color: var(--orator-navy);
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: var(--orator-shadow);
  padding: 1rem;
}

.panel-title {
  margin: 0;
  color: var(--orator-navy);
}

.secondary-button {
  border: 1px solid var(--orator-steel);
  background: white;
  color: var(--orator-navy);
  border-radius: 0.45rem;
  padding: 0.55rem 0.8rem;
  font-weight: 800;
}

.primary-button {
  border: 1px solid var(--orator-blue);
  background: var(--orator-blue);
  color: white;
  border-radius: 0.45rem;
  padding: 0.55rem 0.9rem;
  font-weight: 900;
  box-shadow: 0 2px 6px rgb(36 123 255 / 0.22);
}

.primary-button:hover:not(:disabled) {
  background: #1768e8;
  border-color: #1768e8;
}

button:disabled {
  opacity: 0.6;
}

.notice {
  border-radius: 0.5rem;
  padding: 0.8rem 1rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.notice.error {
  background: #fff1f0;
  color: #b42318;
}

.notice.success {
  background: #ecfdf3;
  color: #027a48;
}

.notice.warning {
  background: #fffbeb;
  color: #92400e;
}

.empty-panel {
  color: var(--orator-graphite);
  padding: 2rem;
  text-align: center;
  border: 1px dashed var(--orator-steel);
  border-radius: 0.5rem;
}

.field-input {
  width: 100%;
  border: 1px solid var(--orator-steel);
  border-radius: 0.45rem;
  padding: 0.55rem 0.65rem;
  box-sizing: border-box;
  color: var(--orator-navy);
  font: inherit;
}

.field-input:disabled {
  background: var(--orator-cloud);
  color: var(--orator-graphite);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.55rem;
  font-weight: 800;
  font-size: 0.78rem;
  background: var(--orator-cloud);
  color: var(--orator-arsenic);
}

.status-pill.visible {
  background: #ecfdf3;
  color: #027a48;
}

.status-pill.preview {
  background: #eff6ff;
  color: var(--orator-blue);
}

.prep-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.bulk-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin: -0.35rem 0 1.25rem;
  padding: 0.75rem;
  background: #f8faff;
  border: 1px solid #dbe7ff;
  border-radius: 0.5rem;
}

.bulk-copy {
  color: var(--orator-graphite);
  font-weight: 750;
}

.prep-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.prep-row {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  background: white;
  display: grid;
  grid-template-columns: 3.25rem 1fr;
  overflow: hidden;
}

.prep-order {
  background: var(--orator-cloud);
  color: var(--orator-arsenic);
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--orator-smoke);
}

.prep-main {
  padding: 1rem;
}

.prep-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.student-name {
  margin: 0;
  color: var(--orator-navy);
  font-size: 1.05rem;
}

.student-meta {
  margin: 0.25rem 0 0;
  color: var(--orator-graphite);
  font-size: 0.9rem;
}

.status-stack {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
}

.prep-grid {
  display: grid;
  grid-template-columns: minmax(180px, 0.7fr) minmax(260px, 1.3fr);
  gap: 0.9rem;
}

.prep-grid label {
  color: var(--orator-graphite);
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.prep-grid .field-input {
  margin-top: 0.35rem;
}

.prep-note {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.45rem;
  background: var(--orator-cloud);
  color: var(--orator-navy);
  padding: 0.75rem;
  font-weight: 750;
  line-height: 1.45;
}

.notes-input {
  min-height: 4.6rem;
  resize: vertical;
}

.check-request-row {
  margin-top: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: var(--orator-navy);
  font-weight: 800;
}

.check-request-row input {
  width: 1rem;
  height: 1rem;
}

.card-actions {
  margin-top: 0.9rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
}

.status-pill.subtle {
  background: var(--orator-cloud);
  color: var(--orator-graphite);
}

.status-pill.warning {
  background: #fff5d9;
  color: #7a4d00;
}

.status-pill.unsaved {
  background: #fff1f0;
  color: #b42318;
}

@media (max-width: 820px) {
  .event-layout {
    flex-direction: column;
  }

  .page-header,
  .prep-summary {
    flex-direction: column;
  }

  .prep-row,
  .prep-grid {
    grid-template-columns: 1fr;
  }

  .prep-order {
    min-height: 2.5rem;
    border-right: 0;
    border-bottom: 1px solid var(--orator-smoke);
  }

  .prep-heading {
    flex-direction: column;
  }

  .status-stack {
    justify-content: flex-start;
  }
}
</style>
